import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../Shared/Loader";
import ApiServices from "../Services/ApiServices";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faArrowRight,
  faArrowLeft,
  faPaperPlane,
  faWindowMaximize,
  faWifi,
  faStar,
  faShieldAlt,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import { formatTime, durationToSeconds } from "../../Helpers/dateHelpers";
import {
  formatExamData,
  createExamSubmissionData,
  getUnansweredQuestions,
} from "../../Helpers/examHelpers";

import MobileBlocker from "../Shared/MobileBlocker";
import { toastHelper } from '../../Helpers/toastHelper';
import { encryptId, encryptData, encryptData as encrypt, decryptData as decrypt } from "../../utils/encryption";
import { useExamSecurity } from "../../Helpers/ioHelpers";
import { getFullFileUrl } from "../../Helpers/UrlHelpers";
import { AppKeys } from "../Shared/AppKeys";
import "../../assets/css/ExamProtectionV1.css";
import Scratchpad from "../Shared/Scratchpad/Scratchpad";
import ExamTracker from "./ExamTracker";

// Text content in different languages
const examTexts = {
  _Arabic: {
    loading: "جاري تحميل الاختبار...",
    examNotFound: "لم يتم العثور على الاختبار",
    backToExams: "العودة إلى قائمة الاختبارات",
    teacher: "أ.",
    progress: "التقدم",
    tabChange: "تغيير التبويب",
    connected: "متصل",
    disconnected: "غير متصل",
    timeRemaining: "الوقت المتبقي",
    question: "السؤال",
    loadingQuestion: "جاري التحميل...",
    finish: "إنهاء",
    next: "التالي",
    previous: "السابق",
    examCompleted: "تم الانتهاء من الاختبار",
    examCompletedMessage: "لقد قمت بإنهاء هذا الاختبار مسبقاً",
    backToList: "العودة للقائمة",
    error: "خطأ",
    errorLoadingExam: "حدث خطأ أثناء تحميل الاختبار",
    errorStartingSession: "حدث خطأ أثناء بدء الجلسة",
    warning: "تنبيه!",
    unansweredQuestions: "لديك {count} سؤال لم تقم بالإجابة عليه. هل تريد إرسال الإجابات؟",
    yesSend: "نعم، إرسال",
    noReturn: "لا، العودة",
    examTimeUp: "انتهى وقت الامتحان!",
    examSubmitted: "تم إرسال الاختبار!",
    examSubmittedMessage: "تم إرسال الاختبار بنجاح",
    ok: "حسناً",
    tabLimitExceeded: "تم تجاوز الحد المسموح لتغيير علامة التبويب",
    errorSubmittingExam: "حدث خطأ أثناء إرسال الاختبار",
    questionWeight: "وزن السؤال",
    questionWeightHint: "كل سؤال يساوي {weight} نقطة"
  },
  _English: {
    loading: "Loading exam...",
    examNotFound: "Exam not found",
    backToExams: "Back to exam list",
    teacher: "Mr.",
    progress: "Progress",
    tabChange: "Tab Change",
    connected: "Connected",
    disconnected: "Disconnected",
    timeRemaining: "Time Remaining",
    question: "Question",
    loadingQuestion: "Loading...",
    finish: "Finish",
    next: "Next",
    previous: "Previous",
    examCompleted: "Exam Completed",
    examCompletedMessage: "You have already completed this exam",
    backToList: "Back to List",
    error: "Error",
    errorLoadingExam: "Error loading exam",
    errorStartingSession: "Error starting session",
    warning: "Warning!",
    unansweredQuestions: "You have {count} unanswered questions. Do you want to submit?",
    yesSend: "Yes, Submit",
    noReturn: "No, Return",
    examTimeUp: "Exam time is up!",
    examSubmitted: "Exam Submitted!",
    examSubmittedMessage: "Exam submitted successfully",
    ok: "OK",
    tabLimitExceeded: "Tab change limit exceeded",
    errorSubmittingExam: "Error submitting exam",
    questionWeight: "Question Weight",
    questionWeightHint: "Each question is worth {weight} points"
  }
};

const ExamProtectionV1 = () => {
  const navigate = useNavigate();
  const { id: encryptedId } = useParams();

  // State management
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [examData, setExamData] = useState(null);
  const [examStarted, setExamStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [examDuration, setExamDuration] = useState(0);
  const [originalExamDuration, setOriginalExamDuration] = useState(0);
  const [tabChanges, setTabChanges] = useState(0);
  const localTimerRef = useRef(null);
  const handleSubmitRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [examLanguage, setExamLanguage] = useState('_Arabic'); // Default to Arabic
  const [textDirection, setTextDirection] = useState('rtl'); // Default to RTL

  // Refs for stable session update loop
  const tabChangesRef = useRef(tabChanges);
  const timeLeftRef = useRef(timeLeft);
  useEffect(() => { tabChangesRef.current = tabChanges; }, [tabChanges]);
  useEffect(() => { timeLeftRef.current = timeLeft; }, [timeLeft]);



  // Helper to determine language and direction based on material
  const determineLanguageAndDirection = (material) => {
    if (material === AppKeys.materials._English || material === AppKeys.materials._French) {
      return {
        language: material === AppKeys.materials._English ? '_English' : '_French',
        direction: 'ltr',
        isRTL: false
      };
    } else {
      // Default to Arabic for any other material
      return {
        language: '_Arabic',
        direction: 'rtl',
        isRTL: true
      };
    }
  };

  // Helper to get text based on current language
  const getText = useCallback((key, replacement = null) => {
    const text = examTexts[examLanguage]?.[key] || examTexts._Arabic[key];
    if (replacement !== null) {
      if (text.includes('{count}')) {
        return text.replace('{count}', replacement);
      }
      if (text.includes('{weight}')) {
        return text.replace('{weight}', replacement);
      }
    }
    return text;
  }, [examLanguage]);

  // Helper to POST session using ApiServices
  const postSession = async (payload, isFirstTime = false) => {
    try {
      const encryptedFirstTime = encryptData(isFirstTime);
      const url = `UserExam/Session/${encryptedFirstTime}`;
      const response = await ApiServices.post(
        url,
        payload,
        false
      );
      return response;
    } catch (error) {
      return {
        check: false,
        msg: error?.msg || error?.message || "Network error",
      };
    }
  };

  // Helper to convert time string back to seconds
  const timeStringToSeconds = (timeString) => {
    if (!timeString) return 0;
    return durationToSeconds(timeString);
  };

  // Main session logic
  useEffect(() => {
    if (!isOnline) return; // Don't start session if offline
    let isMounted = true;
    const startSession = async () => {
      setLoading(true);
      const now = new Date();

      // First, get exam data to get the duration
      const examResponse = await ApiServices.get(`UserExam/${encryptedId}`);

      // Determine language and direction based on material
      if (examResponse.check && examResponse.data && examResponse.data.material) {
        const { language, direction } = determineLanguageAndDirection(examResponse.data.material);
        setExamLanguage(language);
        setTextDirection(direction);
      }

      let initialTimeLeft = "00:00:00";
      if (examResponse.check && examResponse.data && examResponse.data.duration) {
        const originalDuration = durationToSeconds(examResponse.data.duration);
        initialTimeLeft = formatTime(originalDuration);
      }

      const payload = {
        exam_id: encryptedId,
        start_time: now.toISOString(),
        tab_switch_count: encryptId("0"),
        time_left: encrypt(initialTimeLeft), // Encrypt initial time_left
      };

      // On first load, send firstTime = true
      const sessionRes = await postSession(payload, true);
      if (!isMounted) return;

      if (!sessionRes.check) {
        await Swal.fire({
          icon: "error",
          title: getText("error"),
          text: sessionRes.msg || getText("errorStartingSession"),
        });
        setLoading(false);
        return;
      }
      if (sessionRes.check && !sessionRes.data) {
        // No session data exists, use the exam data we already fetched
        const response = examResponse; // Use the exam data we already fetched

        if (response.check && response.data.questions.length > 0) {
          if (response.data.user_exam_status === 3) {
            setExamCompleted(true);
            Swal.fire({
              icon: "warning",
              title: getText("examCompleted"),
              text: getText("examCompletedMessage"),
              confirmButtonText: getText("backToList"),
            }).then(() => {
              navigate("/myexam");
            });
            setLoading(false);
            return;
          }
          const formattedExam = formatExamData(response.data);
          setExamData(formattedExam);
          // Get original exam duration from API
          const originalDuration = durationToSeconds(response.data.duration);

          setOriginalExamDuration(originalDuration);
          setExamDuration(originalDuration);
          setTimeLeft(originalDuration);

          setExamStarted(true);

          // No need to send initial session update since we already sent it with correct time_left
        } else {
          Swal.fire({
            icon: "error",
            title: getText("error"),
            text: response.msg || getText("errorLoadingExam"),
          }).then(() => {
            navigate("/myexam");
          });
        }
        setLoading(false);
      } else if (sessionRes.check && sessionRes.data) {
        // Session data exists, use it
        const session = sessionRes.data;
        setCurrentQuestion(Number(session.current_question_index) || 0);
        setTabChanges(Number(session.tab_switch_count) || 0);
        let parsedAnswers = {};
        try {
          const rawAnswers = JSON.parse(session.answers_json || "{}");
          Object.keys(rawAnswers).forEach(key => {
            parsedAnswers[key] = Number(rawAnswers[key]) - 1;
          });
        } catch (e) {
          parsedAnswers = {};
        }
        setAnswers(parsedAnswers);

        // Fetch exam details to get original duration
        const response = await ApiServices.get(`UserExam/${encryptedId}`);
        if (response.check && response.data.questions.length > 0) {
          if (response.data.user_exam_status === 3) {
            setExamCompleted(true);
            Swal.fire({
              icon: "warning",
              title: getText("examCompleted"),
              text: getText("examCompletedMessage"),
              confirmButtonText: getText("backToList"),
            }).then(() => {
              navigate("/myexam");
            });
            setLoading(false);
            return;
          }
          const formattedExam = formatExamData(response.data);
          setExamData(formattedExam);

          // Get original exam duration from API
          const originalDuration = durationToSeconds(response.data.duration);
          setOriginalExamDuration(originalDuration);

          // Use session time_left if available, else use original exam duration
          let sessionTimeLeft;
          if (session.time_left) {
            // Decrypt time_left and convert to seconds
            const decryptedTimeLeft = decrypt(session.time_left);
            if (decryptedTimeLeft) {
              sessionTimeLeft = timeStringToSeconds(decryptedTimeLeft);
            } else {
              // Fallback to original duration if decryption fails
              sessionTimeLeft = originalDuration;
            }
          } else {
            // No time_left in session, use original exam duration
            sessionTimeLeft = originalDuration;
          }
          setExamDuration(sessionTimeLeft);
          setTimeLeft(sessionTimeLeft);

          setExamStarted(true);
        } else {
          Swal.fire({
            icon: "error",
            title: getText("error"),
            text: response.msg || getText("errorLoadingExam"),
          }).then(() => {
            navigate("/myexam");
          });
        }
        setLoading(false);
      }
    };
    startSession();
    return () => {
      isMounted = false;
    };
  }, [encryptedId, navigate, isOnline, getText]);

  // Local timer for countdown
  useEffect(() => {
    // Clear existing timer
    if (localTimerRef.current) {
      clearInterval(localTimerRef.current);
      localTimerRef.current = null;
    }

    // Start new timer if conditions are met
    if (timeLeft > 0 && examStarted && !examCompleted && examDuration > 0 && isSignalRConnected) {
      localTimerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1;
          return newTime > 0 ? newTime : 0;
        });
      }, 1000);
    }

    return () => {
      if (localTimerRef.current) {
        clearInterval(localTimerRef.current);
      }
    };
  }, [timeLeft, examStarted, examCompleted, examDuration, isSignalRConnected]);

  // Auto-submit if timeLeft reaches 0 (only after exam has actually started)
  useEffect(() => {
    if (timeLeft === 0 && examStarted && !examCompleted && examDuration > 0) {
      handleSubmitRef.current && handleSubmitRef.current(true, getText("examTimeUp"));
    }
  }, [timeLeft, examStarted, examCompleted, examDuration, examLanguage, getText]);

  // Activity tracking (local only, no SignalR)
  useEffect(() => {
    const handleActivity = () => {
      // Placeholder for activity tracking
    };
    document.addEventListener("mousemove", handleActivity);
    document.addEventListener("keydown", handleActivity);
    document.addEventListener("click", handleActivity);
    document.addEventListener("scroll", handleActivity);
    return () => {
      document.removeEventListener("mousemove", handleActivity);
      document.removeEventListener("keydown", handleActivity);
      document.removeEventListener("click", handleActivity);
      document.removeEventListener("scroll", handleActivity);
    };
  }, []);

  // Send status updates when answers change (local only)
  useEffect(() => {
    // Placeholder for answer change effect
  }, [answers]);

  // Tab change detection
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setTabChanges((prev) => prev + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Auto-submit if tabChanges reaches 3
  useEffect(() => {
    if (tabChanges === 3 && !examCompleted) {
      handleSubmitRef.current && handleSubmitRef.current(true, getText("tabLimitExceeded"));
    }
  }, [tabChanges, examCompleted, examLanguage, getText]);

  // Event handlers
  const handleAnswerSelect = (questionId, answerIndex) => {
    if (!isOnline) return;
    const numericIndex = parseInt(answerIndex);
    setAnswers((prev) => ({
      ...prev,
      [questionId]: numericIndex,
    }));
  };

  const handleNext = () => {
    if (!isOnline) return;
    if (currentQuestion < examData?.questions.length - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isOnline) return;
    if (currentQuestion > 0) {
      setCurrentQuestion((prev) => prev - 1);
    }
  };



  const isSubmittingRef = useRef(false);

  const handleSubmit = async (isAutomatic = false, reason = "") => {
    if (!isOnline) return;
    if (!examData || !encryptedId) return;
    if (isSubmittingRef.current) return;
    
    const unanswered = getUnansweredQuestions(examData.questions, answers);
    if (unanswered.length > 0 && !isAutomatic) {
      const result = await Swal.fire({
        title: getText("warning"),
        text: getText("unansweredQuestions", unanswered.length),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: getText("yesSend"),
        cancelButtonText: getText("noReturn"),
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
      });
      if (!result.isConfirmed) return;
    }
    try {
      isSubmittingRef.current = true;
      setSubmitLoading(true);
      const allAnswers = { ...answers };
      unanswered.forEach((q) => {
        allAnswers[q.id] = -1;
      });
      const formData = createExamSubmissionData(
        encryptedId,
        examData.questions,
        allAnswers
      );

      // Calculate time spent on exam (Original Duration - Time Left)
      // Ensure we don't send a negative value
      const timeSpentSeconds = Math.max(0, originalExamDuration - timeLeft);
      const timeEstimation = formatTime(timeSpentSeconds);

      // Append time_estimation to formData for C# TimeSpan compatibility
      formData.append('time_estimation', timeEstimation);
      const response = await ApiServices.post(
        "UserExam/SendAnswer",
        formData,
        true
      );
      if (response.check) {
        setExamCompleted(true);
        await Swal.fire({
          title: isAutomatic ? getText("examTimeUp") : getText("examSubmitted"),
          text: reason || getText("examSubmittedMessage"),
          icon: "success",
          confirmButtonText: getText("ok"),
        });
        navigate("/myexam", { replace: true });
      } else {
        throw new Error("Failed to submit exam");
      }
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : error?.msg || error?.message || getText("errorSubmittingExam");
      toastHelper.error(errorMessage);
      isSubmittingRef.current = false;
    } finally {
      setSubmitLoading(false);
    }
  };

  handleSubmitRef.current = handleSubmit;

  const handleForceClose = useCallback((reason) => {
    if (!examCompleted) {
      handleSubmitRef.current && handleSubmitRef.current(true, reason);
    }
  }, [examCompleted]);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(
        window.innerWidth <= 768 || /Mobi|Android/i.test(navigator.userAgent)
      );
    };
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Internet connection status effect
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // في أعلى الكومبوننت مباشرة بعد تعريف ExamProtectionV1
  useExamSecurity();

  if (loading) return <Loader text={getText("loading")} />;
  if (!examData) {
    return (
      <div className="exam-error" style={{ direction: textDirection }}>
        <h3>{getText("examNotFound")}</h3>
        <button onClick={() => navigate("/myexam")}>
          {getText("backToExams")}
        </button>
      </div>
    );
  }

  const getConnectionStatusText = () =>
    isOnline ? getText("connected") : getText("disconnected");

  // Calculate progress percentage
  const progressPercentage = ((currentQuestion + 1) / examData.questions.length) * 100;

  const trackerComponent = (
    <ExamTracker
      examId={encryptedId}
      timeLeft={encrypt(formatTime(timeLeft))}
      tabSwitchCount={encryptId(String(tabChanges))}
      rawTabSwitchCount={tabChanges}
      onForceClose={handleForceClose}
      isOnline={isOnline}
      onConnectionStateChange={(status) => setIsSignalRConnected(status)}
    />
  );

  const reconnectingOverlay = (
    <AnimatePresence>
      {!isSignalRConnected && examStarted && !examCompleted && (
        <motion.div
          style={{ 
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
            background: 'rgba(15, 23, 42, 0.7)', 
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' 
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div 
            style={{ 
              background: 'rgba(255, 255, 255, 0.05)', 
              border: '1px solid rgba(255, 255, 255, 0.1)',
              padding: '3rem 4rem', 
              borderRadius: '24px', 
              textAlign: 'center', 
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1.5rem',
              direction: textDirection
            }}
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(59, 130, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
              }}
            >
              <FontAwesomeIcon icon={faWifi} style={{ fontSize: '2.5rem', color: '#60A5FA' }} />
            </motion.div>
            
            <div>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, letterSpacing: '0.5px' }}>
                {examLanguage === '_Arabic' ? "انقطع الاتصال بالخادم" : "Connection Lost"}
              </h3>
              <p style={{ margin: '0.5rem 0 0', fontSize: '1rem', color: '#94A3B8' }}>
                {examLanguage === '_Arabic' ? "جاري محاولة إعادة الاتصال..." : "Attempting to reconnect..."}
              </p>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem' }}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                  style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#60A5FA' }}
                />
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Mobile check
  if (isMobile) {
    return (
      <MobileBlocker message="هذا الاختبار متاح فقط على أجهزة الكمبيوتر والتابلت. يرجى فتح الاختبار من لابتوب أو تابلت." navigationBack="/myexam">
        {trackerComponent}
        {reconnectingOverlay}
        <div className="exam-container-new" style={{ direction: textDirection }}>
          {/* Main Exam Card */}
          <div className="exam-card">
            {/* Header Section */}
            <div className="exam-header-new">
              <div className="header-left">
                <h2 className="exam-title">{examData?.title || "اختبار تجريبي"}</h2>
                <p className="session-info">{getText("teacher")} {examData?.teacher || "المعلم"}</p>
              </div>

              <div className="header-center">
                <div className="progress-section">
                  <div className="progress-bar-new">
                    <div
                      className="progress-fill-new"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">{Math.round(progressPercentage)}%</span>
                </div>
              </div>
            </div>

            {/* Exam Stats Bar */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px', padding: '15px 20px' }}>
              <div className={`connection-status-new ${isOnline ? 'online' : 'offline'}`}>
                <FontAwesomeIcon icon={faWifi} className="connection-icon" />
                <span className="connection-text">{getConnectionStatusText()}</span>
              </div>
              <div className="timer-section">
                <FontAwesomeIcon icon={faClock} className="timer-icon" />
                <div className="timer-info">
                  <span className="timer-value">{formatTime(timeLeft)}</span>
                  <span className="timer-label">{getText("timeRemaining")}</span>
                </div>
              </div>
              <div className="tab-warning-center">
                <motion.div
                  className={`tab-warning-section ${tabChanges >= 2 ? 'warning' : ''}`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <FontAwesomeIcon icon={faWindowMaximize} className="tab-icon" />
                  <div className="tab-info">
                    <span className={`tab-value ${tabChanges >= 2 ? 'warning' : ''}`}>{tabChanges}/3</span>
                    <span className="tab-label">{getText("tabChange")}</span>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Full-width Anti-Cheating Protection Badge */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 20px', marginBottom: '20px' }}>
              <motion.div
                className="anti-cheat-badge"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
                style={{ marginTop: '0', width: '100%', maxWidth: '1000px', justifyContent: 'center' }}
              >
                <motion.div
                  className="badge-shield"
                  animate={{ rotate: [0, -5, 5, -5, 0] }}
                  transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                >
                  <FontAwesomeIcon icon={faShieldAlt} className="shield-icon" />
                </motion.div>
                <div className="badge-content" style={{ flex: 'none', alignItems: 'center' }}>
                  <span className="badge-title">
                    {examLanguage === '_Arabic' ? 'حماية شاملة من الغش' : examLanguage === '_English' ? 'Full Anti-Cheating Protection' : 'Protection Anti-Triche Complète'}
                  </span>
                  <div className="badge-features" style={{ justifyContent: 'center' }}>
                    <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }}>
                      <FontAwesomeIcon icon={faEye} className="feature-icon" />
                      {examLanguage === '_Arabic' ? 'مراقبة نشطة' : examLanguage === '_English' ? 'Active Monitoring' : 'Surveillance Active'}
                    </motion.span>
                    <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}>
                      <FontAwesomeIcon icon={faWindowMaximize} className="feature-icon" />
                      {examLanguage === '_Arabic' ? 'تتبع التبويبات' : examLanguage === '_English' ? 'Tab Tracking' : 'Suivi des onglets'}
                    </motion.span>
                    <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity, delay: 1.2 }}>
                      <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                      {examLanguage === '_Arabic' ? 'اتصال آمن' : examLanguage === '_English' ? 'Secure Connection' : 'Connexion Sécurisée'}
                    </motion.span>
                  </div>
                </div>
                <div className="badge-glow"></div>
              </motion.div>
            </div>

            {/* Question Section */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion}
                className="question-section"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{
                  duration: 0.4,
                  ease: "easeInOut"
                }}
              >
                <motion.h3
                  className="question-number"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1, duration: 0.3 }}
                >
                  {getText("question")} {currentQuestion + 1}
                </motion.h3>
                <motion.p
                  className="question-text-new"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  {examData.questions[currentQuestion]?.question || getText("loadingQuestion")}
                </motion.p>

                {examData.questions[currentQuestion]?.question_image && (
                  <motion.div
                    className="question-image-container-new"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.25, duration: 0.4 }}
                  >
                    <img
                      src={getFullFileUrl(examData.questions[currentQuestion].question_image)}
                      alt="Question Illustration"
                      className="question-image-new"
                      onClick={() => {
                        Swal.fire({
                          imageUrl: getFullFileUrl(examData.questions[currentQuestion].question_image),
                          imageAlt: "Question Image",
                          showCloseButton: true,
                          showConfirmButton: false,
                          width: 'auto',
                          background: 'rgba(255, 255, 255, 0.9)',
                          backdrop: 'rgba(0,0,0,0.8)'
                        });
                      }}
                      style={{ cursor: 'zoom-in' }}
                    />
                  </motion.div>
                )}

                {/* Question Weight Hint */}
                {examData.question_weight && examData.question_weight !== 1 && (
                  <motion.div
                    className="question-weight-hint"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
                  >
                    <div className="weight-icon-container">
                      <FontAwesomeIcon icon={faStar} className="weight-icon" />
                    </div>
                    <div className="weight-content">
                      <span className="weight-label">{getText("questionWeight")}</span>
                      <span className="weight-value">{examData.question_weight}</span>
                      <span className="weight-hint-text">
                        {getText("questionWeightHint", examData.question_weight)}
                      </span>
                    </div>
                    <div className="weight-glow"></div>
                  </motion.div>
                )}

                <div className="options-container-new">
                  {examData.questions[currentQuestion]?.options?.map((option, index) => (
                    <motion.div
                      key={index}
                      className={`option-new ${answers[examData.questions[currentQuestion]?.id] === index ? 'selected' : ''
                        }`}
                      onClick={() => handleAnswerSelect(examData.questions[currentQuestion]?.id, index)}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: 0.3 + (index * 0.1),
                        duration: 0.3,
                        ease: "easeOut"
                      }}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)"
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="option-content">
                        <span className="option-text-new">{option}</span>
                        <div className="radio-button">
                          {answers[examData.questions[currentQuestion]?.id] === index && (
                            <motion.div
                              className="radio-fill"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ duration: 0.2, ease: "easeOut" }}
                            />
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Footer Navigation */}
            <div className="exam-footer-new">
              {currentQuestion === examData.questions.length - 1 ? (
                <motion.button
                  className="finish-btn"
                  onClick={handleSubmit}
                  disabled={submitLoading}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  {submitLoading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      {getText("finish")}
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </>
                  )}
                </motion.button>
              ) : (
                <motion.button
                  className="nav-btn next-btn"
                  onClick={handleNext}
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  {getText("next")}
                  <FontAwesomeIcon icon={faArrowRight} />
                </motion.button>
              )}

              <motion.button
                className="nav-btn prev-btn"
                onClick={handlePrevious}
                disabled={currentQuestion === 0}
                whileHover={{ scale: 1.05, x: -5 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                {getText("previous")}
              </motion.button>
            </div>
          </div>
        </div>
      </MobileBlocker>
    );
  }

  // Desktop render
  return (
    <div className="exam-container-new" style={{ direction: textDirection }}>
      {trackerComponent}
      {reconnectingOverlay}
      {/* Main Exam Card */}
      <div className="exam-card">
        {/* Header Section */}
        <div className="exam-header-new">
          <div className="header-left">
            <h2 className="exam-title">{examData?.title || "اختبار تجريبي"}</h2>
            <p className="session-info">{getText("teacher")} {examData?.teacher || "المعلم"}</p>
          </div>

            <div className="header-center">
              <div className="progress-section">
                <div className="progress-bar-new">
                  <div
                    className="progress-fill-new"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
                <span className="progress-text">{Math.round(progressPercentage)}%</span>
              </div>
            </div>
          </div>

          {/* Exam Stats Bar */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '15px', padding: '15px 20px' }}>
            <div className={`connection-status-new ${isOnline ? 'online' : 'offline'}`}>
              <FontAwesomeIcon icon={faWifi} className="connection-icon" />
              <span className="connection-text">{getConnectionStatusText()}</span>
            </div>
            <div className="timer-section">
              <FontAwesomeIcon icon={faClock} className="timer-icon" />
              <div className="timer-info">
                <span className="timer-value">{formatTime(timeLeft)}</span>
                <span className="timer-label">{getText("timeRemaining")}</span>
              </div>
            </div>
            <div className="tab-warning-center">
              <motion.div
                className={`tab-warning-section ${tabChanges >= 2 ? 'warning' : ''}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <FontAwesomeIcon icon={faWindowMaximize} className="tab-icon" />
                <div className="tab-info">
                  <span className={`tab-value ${tabChanges >= 2 ? 'warning' : ''}`}>{tabChanges}/3</span>
                  <span className="tab-label">{getText("tabChange")}</span>
                </div>
              </motion.div>
            </div>
          </div>

        {/* Full-width Anti-Cheating Protection Badge */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', padding: '0 20px', marginBottom: '20px' }}>
          <motion.div
            className="anti-cheat-badge"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
            style={{ marginTop: '0', width: '100%', maxWidth: '1000px', justifyContent: 'center' }}
          >
            <motion.div
              className="badge-shield"
              animate={{ rotate: [0, -5, 5, -5, 0] }}
              transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
            >
              <FontAwesomeIcon icon={faShieldAlt} className="shield-icon" />
            </motion.div>
            <div className="badge-content" style={{ flex: 'none', alignItems: 'center' }}>
              <span className="badge-title">
                {examLanguage === '_Arabic' ? 'حماية شاملة من الغش' : examLanguage === '_English' ? 'Full Anti-Cheating Protection' : 'Protection Anti-Triche Complète'}
              </span>
              <div className="badge-features" style={{ justifyContent: 'center' }}>
                <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }}>
                  <FontAwesomeIcon icon={faEye} className="feature-icon" />
                  {examLanguage === '_Arabic' ? 'مراقبة نشطة' : examLanguage === '_English' ? 'Active Monitoring' : 'Surveillance Active'}
                </motion.span>
                <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}>
                  <FontAwesomeIcon icon={faWindowMaximize} className="feature-icon" />
                  {examLanguage === '_Arabic' ? 'تتبع التبويبات' : examLanguage === '_English' ? 'Tab Tracking' : 'Suivi des onglets'}
                </motion.span>
                <motion.span className="feature-item" animate={{ opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity, delay: 1.2 }}>
                  <FontAwesomeIcon icon={faShieldAlt} className="feature-icon" />
                  {examLanguage === '_Arabic' ? 'اتصال آمن' : examLanguage === '_English' ? 'Secure Connection' : 'Connexion Sécurisée'}
                </motion.span>
              </div>
            </div>
            <div className="badge-glow"></div>
          </motion.div>
        </div>

        {/* Question Section */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            className="question-section"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{
              duration: 0.4,
              ease: "easeInOut"
            }}
          >
            <motion.h3
              className="question-number"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              {getText("question")} {currentQuestion + 1}
            </motion.h3>
            <motion.p
              className="question-text-new"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {examData.questions[currentQuestion]?.question || getText("loadingQuestion")}
            </motion.p>

            {examData.questions[currentQuestion]?.question_image && (
              <motion.div
                className="question-image-container-new"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.25, duration: 0.4 }}
              >
                <img
                  src={getFullFileUrl(examData.questions[currentQuestion].question_image)}
                  alt="Question Illustration"
                  className="question-image-new"
                  onClick={() => {
                    Swal.fire({
                      imageUrl: getFullFileUrl(examData.questions[currentQuestion].question_image),
                      imageAlt: "Question Image",
                      showCloseButton: true,
                      showConfirmButton: false,
                      width: 'auto',
                      background: 'rgba(255, 255, 255, 0.9)',
                      backdrop: 'rgba(0,0,0,0.8)'
                    });
                  }}
                  style={{ cursor: 'zoom-in' }}
                />
              </motion.div>
            )}

            {/* Question Weight Hint */}
            {examData.question_weight && examData.question_weight !== 1 && (
              <motion.div
                className="question-weight-hint"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
              >
                <div className="weight-icon-container">
                  <FontAwesomeIcon icon={faStar} className="weight-icon" />
                </div>
                <div className="weight-content">
                  <span className="weight-label">{getText("questionWeight")}</span>
                  <span className="weight-value">{examData.question_weight}</span>
                  <span className="weight-hint-text">
                    {getText("questionWeightHint", examData.question_weight)}
                  </span>
                </div>
                <div className="weight-glow"></div>
              </motion.div>
            )}

            <div className="options-container-new">
              {examData.questions[currentQuestion]?.options?.map((option, index) => (
                <motion.div
                  key={index}
                  className={`option-new ${answers[examData.questions[currentQuestion]?.id] === index ? 'selected' : ''
                    }`}
                  onClick={() => handleAnswerSelect(examData.questions[currentQuestion]?.id, index)}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{
                    delay: 0.3 + (index * 0.1),
                    duration: 0.3,
                    ease: "easeOut"
                  }}
                  whileHover={{
                    scale: 1.02,
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="option-content">
                    <span className="option-text-new">{option}</span>
                    <div className="radio-button">
                      {answers[examData.questions[currentQuestion]?.id] === index && (
                        <motion.div
                          className="radio-fill"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                        />
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Footer Navigation */}
        <div className="exam-footer-new">
          {currentQuestion === examData.questions.length - 1 ? (
            <motion.button
              className="finish-btn"
              onClick={handleSubmit}
              disabled={submitLoading}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              {submitLoading ? (
                <span className="loading-spinner"></span>
              ) : (
                <>
                  {getText("finish")}
                  <FontAwesomeIcon icon={faPaperPlane} />
                </>
              )}
            </motion.button>
          ) : (
            <motion.button
              className="nav-btn next-btn"
              onClick={handleNext}
              whileHover={{ scale: 1.05, x: 5 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              {getText("next")}
              <FontAwesomeIcon icon={faArrowRight} />
            </motion.button>
          )}

          <motion.button
            className="nav-btn prev-btn"
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {getText("previous")}
          </motion.button>
        </div>
      </div>
      {examData?.use_scratchpad && <Scratchpad canDownload={false} />}
    </div>
  );
};

export default ExamProtectionV1;
